import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api } from "../lib/api";
import { waLink, EVENT_TYPES } from "../lib/constants";

const empty = {
  name: "",
  phone: "",
  event_type: "",
  event_date: "",
  guests: "",
  message: "",
};

export default function EnquiryForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const service = params.get("service");
    if (service) {
      setForm((f) => ({
        ...f,
        message: f.message || `Hi! I'd like to enquire about ${service}.`,
        event_type: EVENT_TYPES.includes(service) ? service : f.event_type || "Other",
      }));
    }
  }, [params]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const buildWaMessage = () =>
    [
      "Hello Jaiswal Tent House! I have an event enquiry.",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Event Type: ${form.event_type || "-"}`,
      form.event_date ? `Event Date: ${form.event_date}` : "",
      form.guests ? `Guests: ${form.guests}` : "",
      form.message ? `Message: ${form.message}` : "",
    ]
      .filter(Boolean)
      .join("\n");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    let saved = false;
    try {
      await api.post("/enquiries", form);
      saved = true;
      toast.success("Enquiry received! We will contact you soon.");
    } catch {
      toast.error("Could not save the enquiry — opening WhatsApp for you instead.");
    } finally {
      window.open(waLink(buildWaMessage()), "_blank");
      setLoading(false);
      if (saved) setForm(empty);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          className="field"
          placeholder="Your Name *"
          value={form.name}
          onChange={set("name")}
          data-testid="enquiry-name-input"
        />
        <input
          className="field"
          placeholder="Phone Number *"
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          data-testid="enquiry-phone-input"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <select
          className="field appearance-none"
          value={form.event_type}
          onChange={set("event_type")}
          data-testid="enquiry-event-type-select"
        >
          <option value="">Event Type</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t} className="bg-[#14120E]">
              {t}
            </option>
          ))}
        </select>
        <input
          className="field"
          type="date"
          value={form.event_date}
          onChange={set("event_date")}
          data-testid="enquiry-date-input"
        />
      </div>
      <input
        className="field"
        placeholder="Approx. Number of Guests"
        value={form.guests}
        onChange={set("guests")}
        data-testid="enquiry-guests-input"
      />
      <textarea
        className="field min-h-28 resize-y"
        placeholder="Tell us about your event..."
        value={form.message}
        onChange={set("message")}
        data-testid="enquiry-message-input"
      />
      <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60" data-testid="enquiry-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Sending..." : "Send Enquiry on WhatsApp"}
      </button>
    </form>
  );
}
